import { AlertTriangle, Check, Info, Lock, Package } from 'lucide-react';
import { PosBackButton } from '../../components/PosBackButton';
import type { CountStatus } from './stockCountTypes';
import { countStatusLabel } from './stockCountTypes';

export function InvStatusBadge({ status }: { status: CountStatus }) {
  const label = countStatusLabel(status);
  const styles: Record<CountStatus, string> = {
    draft: 'bg-gray-800 text-gray-400 border border-gray-700',
    in_progress: 'bg-brand/15 text-brand border border-brand/30',
    review: 'bg-amber-950/50 text-amber-400 border border-amber-800',
    closed: 'bg-emerald-950/50 text-emerald-400 border border-emerald-800',
    cancelled: 'bg-red-950/50 text-red-400 border border-red-800',
  };
  const dots: Record<CountStatus, string> = {
    draft: 'bg-gray-500',
    in_progress: 'bg-brand',
    review: 'bg-amber-500',
    closed: 'bg-emerald-500',
    cancelled: 'bg-red-500',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${styles[status]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dots[status]}`} />
      {label}
    </span>
  );
}

export function ProgressRingLabel({
  counted,
  total,
  percent,
  size = 44,
}: {
  counted?: number;
  total?: number;
  percent?: number;
  size?: number;
}) {
  const pct =
    percent != null
      ? Math.min(percent / 100, 1)
      : total && total > 0
        ? Math.min((counted ?? 0) / total, 1)
        : 0;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#374151"
          strokeWidth={4}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#0D7377"
          strokeWidth={4}
          strokeDasharray={`${circ * pct} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[9px] font-bold text-brand tabular-nums">
          {Math.round(pct * 100)}%
        </span>
      </div>
    </div>
  );
}

export function VBadge({
  variance,
  high,
}: {
  variance: number | null;
  high?: boolean;
}) {
  if (variance === null) {
    return <span className="text-[10px] text-gray-600 tabular-nums">—</span>;
  }
  if (variance === 0) {
    return (
      <span className="text-[10px] text-gray-500 font-semibold tabular-nums">0</span>
    );
  }
  if (variance < 0) {
    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${
          high ? 'bg-red-950/60 text-red-400' : 'bg-red-950/30 text-red-400'
        }`}
      >
        {high && <AlertTriangle size={8} />}
        {variance}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${
        high ? 'bg-amber-950/60 text-amber-400' : 'bg-emerald-950/40 text-emerald-400'
      }`}
    >
      +{variance}
    </span>
  );
}

export function CrossAppBanner({ id }: { id: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-sky-950/40 border-b border-sky-900/40 shrink-0">
      <Info size={11} className="text-sky-400 shrink-0" />
      <span className="text-[11px] text-sky-400 font-medium">
        Session partagée — continuable depuis l&apos;autre application
      </span>
      <span className="ml-auto text-[10px] text-sky-600 font-mono">{id}</span>
    </div>
  );
}

export function StockCountAccessDenied({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col h-full bg-brand-dark min-h-0">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 shrink-0">
        <PosBackButton onClick={onBack} />
        <span className="text-sm font-bold text-white">Inventaire</span>
      </div>
      <div className="flex flex-col items-center justify-center flex-1 gap-5">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-red-900/30">
          <Lock size={28} className="text-red-400" />
        </div>
        <div className="text-center space-y-1">
          <div className="font-bold text-lg text-white">Accès refusé</div>
          <div className="text-sm text-gray-500">
            L&apos;inventaire physique est réservé aux
            <br />
            responsables et administrateurs.
          </div>
        </div>
        <div className="px-4 py-2.5 rounded-xl text-xs font-medium text-center max-w-xs bg-red-900/20 border border-red-800/30 text-red-400">
          Rôle requis: Responsable / Administrateur
        </div>
      </div>
    </div>
  );
}

export function StepBarDark({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center gap-0 w-full mb-6">
      {steps.map((label, i) => {
        const num = i + 1;
        const done = num < current;
        const active = num === current;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                  done
                    ? 'bg-brand border-brand text-white'
                    : active
                      ? 'bg-gray-900 border-brand text-brand'
                      : 'bg-gray-900 border-gray-700 text-gray-600'
                }`}
              >
                {done ? <Check size={14} /> : num}
              </div>
              <span
                className={`text-[10px] font-semibold whitespace-nowrap ${
                  active ? 'text-brand' : done ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 mb-4 ${done ? 'bg-brand' : 'bg-gray-800'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ProductThumb({
  src,
  alt,
  size = 'md',
}: {
  src: string | null | undefined;
  alt: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim = size === 'sm' ? 'w-7 h-7' : size === 'lg' ? 'w-20 h-20' : 'w-10 h-10';
  if (!src) {
    return (
      <div
        className={`${dim} rounded-lg bg-gray-800 flex items-center justify-center shrink-0`}
      >
        <Package size={size === 'lg' ? 28 : 14} className="text-gray-600" />
      </div>
    );
  }
  return (
    <img src={src} alt={alt} className={`${dim} rounded-lg object-cover shrink-0`} />
  );
}

export function QuantityKeypad({
  onKey,
}: {
  onKey: (k: string) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'].map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onKey(k)}
          className={`py-4 rounded-xl text-xl font-bold transition-all active:scale-95 min-h-[48px] ${
            k === 'OK'
              ? 'bg-brand text-white'
              : k === '⌫'
                ? 'bg-gray-800 text-gray-400'
                : 'bg-gray-900 border border-gray-800 text-white hover:bg-gray-800'
          }`}
        >
          {k}
        </button>
      ))}
    </div>
  );
}
